import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

import { logger } from '../../../utils/logger';
import { formatDistanceToNow } from 'date-fns';

const NotificationHistoryTab = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadNotificationHistory();
  }, [userId]);

  useEffect(() => {
    applyFilters();
  }, [notifications, searchQuery, filterType, filterStatus]);

  const loadNotificationHistory = async () => {
    try {
      setLoading(true);
      // Mock data for demonstration - replace with actual API call
      const mockNotifications = [
        {
          id: '1',
          type: 'security_violation',
          title: 'Unauthorized Access Attempt',
          message: 'Failed login attempt detected from unknown location',
          severity: 9,
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
          status: 'delivered',
          channel: 'email'
        },
        {
          id: '2',
          type: 'data_modification',
          title: 'Symptom Entry Updated',
          message: 'User modified symptom entry for managed profile',
          severity: 5,
          timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
          status: 'delivered',
          channel: 'in-app'
        },
        {
          id: '3',
          type: 'access_attempt',
          title: 'Data Export Completed',
          message: 'Healthcare provider exported symptom summary',
          severity: 6,
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
          status: 'batched',
          channel: 'email'
        },
        {
          id: '4',
          type: 'system_event',
          title: 'Settings Updated',
          message: 'Notification preferences were modified',
          severity: 3,
          timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000),
          status: 'delivered',
          channel: 'in-app'
        }
      ];
      setNotifications(mockNotifications);
    } catch (error) {
      logger?.error('Failed to load notification history', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...notifications];

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered?.filter(n => n?.type === filterType);
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered?.filter(n => n?.status === filterStatus);
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(n => 
        n?.title?.toLowerCase()?.includes(query) ||
        n?.message?.toLowerCase()?.includes(query)
      );
    }

    setFilteredNotifications(filtered);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'security_violation': return 'ShieldAlert';
      case 'data_modification': return 'Edit';
      case 'access_attempt': return 'Eye';
      case 'system_event': return 'Server';
      default: return 'Bell';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'security_violation': return 'text-error';
      case 'data_modification': return 'text-warning';
      case 'access_attempt': return 'text-primary';
      case 'system_event': return 'text-muted-foreground';
      default: return 'text-foreground';
    }
  };

  const getSeverityColor = (severity) => {
    if (severity >= 8) return 'bg-error text-error-foreground';
    if (severity >= 5) return 'bg-warning text-warning-foreground';
    return 'bg-success text-success-foreground';
  };

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'security_violation', label: 'Security Violations' },
    { value: 'data_modification', label: 'Data Modifications' },
    { value: 'access_attempt', label: 'Access Attempts' },
    { value: 'system_event', label: 'System Events' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'batched', label: 'Batched' },
    { value: 'failed', label: 'Failed' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Input
            type="text"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="sm:col-span-1"
          />
          <Select
            value={filterType}
            onChange={setFilterType}
            options={typeOptions}
            placeholder="Filter by type"
          />
          <Select
            value={filterStatus}
            onChange={setFilterStatus}
            options={statusOptions}
            placeholder="Filter by status"
          />
        </div>
      </div>

      {/* Notification List */}
      {filteredNotifications?.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-8 sm:p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
              <Icon name="Bell" size={32} className="text-muted-foreground" strokeWidth={2} />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Notifications Found</h3>
          <p className="text-sm text-muted-foreground">
            {searchQuery || filterType !== 'all' || filterStatus !== 'all' ?'Try adjusting your filters' :'You have no notification history yet'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications?.map((notification) => (
            <div
              key={notification?.id}
              className="bg-card rounded-xl border border-border p-4 sm:p-6 hover:shadow-therapeutic transition-smooth"
            >
              <div className="flex items-start gap-4">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 flex-shrink-0`}>
                  <Icon 
                    name={getTypeIcon(notification?.type)} 
                    size={20} 
                    className={getTypeColor(notification?.type)}
                    strokeWidth={2} 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-foreground mb-1">
                        {notification?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {notification?.message}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${getSeverityColor(notification?.severity)}`}>
                      Severity {notification?.severity}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={14} strokeWidth={2} />
                      {formatDistanceToNow(notification?.timestamp, { addSuffix: true })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Send" size={14} strokeWidth={2} />
                      {notification?.channel}
                    </span>
                    <span className={`px-2 py-0.5 rounded-md ${
                      notification?.status === 'delivered' ? 'bg-success/10 text-success' :
                      notification?.status === 'batched'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                    }`}>
                      {notification?.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border border-border p-4 text-center">
          <div className="text-2xl font-bold text-foreground">{notifications?.length}</div>
          <div className="text-xs text-muted-foreground mt-1">Total Notifications</div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4 text-center">
          <div className="text-2xl font-bold text-success">
            {notifications?.filter(n => n?.status === 'delivered')?.length}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Delivered</div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4 text-center">
          <div className="text-2xl font-bold text-warning">
            {notifications?.filter(n => n?.status === 'batched')?.length}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Batched</div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4 text-center">
          <div className="text-2xl font-bold text-error">
            {notifications?.filter(n => n?.severity >= 8)?.length}
          </div>
          <div className="text-xs text-muted-foreground mt-1">Critical</div>
        </div>
      </div>
    </div>
  );
};

export default NotificationHistoryTab;